import { useEffect, useState } from "react";
import api from "../lib/api";
import { KanbanBoard } from "../components/KanbanBoard";
import { DataPorter } from "../components/DataPorter";
import { FunnelList, ViewToggle } from "../components/FunnelList";
import { CLINICIAN_STAGES, INDIA_CITIES, CRED_META, labelOf } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, MapPin, Award, AlertTriangle, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const empty = { name: "", qualifications: "", years_experience: 0, city: "Chennai", service_radius_km: 10, capacity_sessions_week: 20, credentialing_status: "pending", notes: "" };

export default function CliniciansFunnel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list");
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [cedit, setCEdit] = useState(null);

  const load = () => api.get("/clinicians").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const move = async (item, stage) => {
    await api.patch(`/clinicians/${item._id}/stage`, { stage });
    toast.success(`Moved to ${labelOf(CLINICIAN_STAGES, stage)}`);
    load();
  };

  const create = async () => {
    if (!form.name) return toast.error("Name required");
    await api.post("/clinicians", {
      ...form,
      qualifications: form.qualifications.split(",").map((q) => q.trim()).filter(Boolean),
      years_experience: Number(form.years_experience),
      service_radius_km: Number(form.service_radius_km),
      capacity_sessions_week: Number(form.capacity_sessions_week),
    });
    toast.success("Clinician added");
    setOpen(false); setForm(empty); load();
  };

  const openDetail = async (item) => {
    const { data } = await api.get(`/clinicians/${item._id}`);
    setDetail(data);
    setCEdit({ stage: data.stage, credentialing_status: data.credentialing_status, capacity_sessions_week: data.capacity_sessions_week || 0, city: data.city || "", notes: data.notes || "" });
  };

  const saveClinician = async () => {
    await api.patch(`/clinicians/${detail._id}`, { ...cedit, capacity_sessions_week: Number(cedit.capacity_sessions_week) || 0 });
    toast.success("Clinician updated");
    const { data } = await api.get(`/clinicians/${detail._id}`); setDetail(data); load();
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${detail.name}? This cannot be undone.`)) return;
    await api.delete(`/clinicians/${detail._id}`);
    toast.success("Clinician deleted");
    setDetail(null); load();
  };
  const activeCount = items.filter((i) => i.stage === "active").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-head text-ccc-navy" data-testid="clinicians-title">Clinician Sourcing</h1>
          <p className="text-sm text-slate-500 mt-1">Supply-side physiotherapists · {activeCount} active · credentialing-synced</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <DataPorter entity="clinicians" onImported={load} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-clinician-button" className="bg-ccc-blue hover:bg-ccc-blue2 text-white"><Plus className="h-4 w-4 mr-1.5" /> Add Clinician</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Clinician</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="col-span-2"><Label>Name</Label><Input data-testid="clinician-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div className="col-span-2"><Label>Qualifications (comma-separated)</Label><Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} placeholder="BPT, MPT Ortho" className="mt-1" /></div>
              <div><Label>Years experience</Label><Input type="number" value={form.years_experience} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} className="mt-1" /></div>
              <div><Label>City</Label>
                <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{INDIA_CITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Service radius (km)</Label><Input type="number" value={form.service_radius_km} onChange={(e) => setForm({ ...form, service_radius_km: e.target.value })} className="mt-1" /></div>
              <div><Label>Capacity (sessions/wk)</Label><Input type="number" value={form.capacity_sessions_week} onChange={(e) => setForm({ ...form, capacity_sessions_week: e.target.value })} className="mt-1" /></div>
              <div><Label>Credentialing status</Label>
                <Select value={form.credentialing_status} onValueChange={(v) => setForm({ ...form, credentialing_status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(CRED_META).map((s) => <SelectItem key={s} value={s}>{CRED_META[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button data-testid="clinician-save-button" onClick={create} className="bg-ccc-blue hover:bg-ccc-blue2 text-white">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <ViewToggle view={view} setView={setView} />
      <div className="mt-4" />
      {view === "list" && (
        <FunnelList
          items={items}
          onRowClick={openDetail}
          testid="clinicians-list"
          columns={[
            { label: "Name", sortKey: "name", render: (c) => <span className="font-medium text-ccc-navy">{c.name}</span> },
            { label: "City", sortKey: "city", render: (c) => c.city || "—" },
            { label: "Experience", sortKey: "years_experience", render: (c) => `${c.years_experience || 0}y` },
            { label: "Capacity", sortKey: "capacity_sessions_week", render: (c) => `${c.capacity_sessions_week || 0}/wk` },
            { label: "Credentialing", sortKey: "credentialing_status", render: (c) => { const m = CRED_META[c.credentialing_status] || CRED_META.pending; return <Badge className={`${m.cls} border-0 text-[10px]`}>{m.label}</Badge>; } },
            { label: "Stage", sortKey: "stage", render: (c) => <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{labelOf(CLINICIAN_STAGES, c.stage)}</Badge> },
          ]}
        />
      )}
      {view === "board" && (
      <KanbanBoard
        stages={CLINICIAN_STAGES}
        items={items}
        accent="#0D9488"
        onMove={move}
        onCardClick={openDetail}
        renderCard={(c) => {
          const meta = CRED_META[c.credentialing_status] || CRED_META.pending;
          return (
            <div>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-sm text-ccc-navy leading-tight">{c.name}</p>
                {c.credentialing_flags?.length > 0 && <AlertTriangle className="h-4 w-4 text-ccc-rose flex-shrink-0" />}
              </div>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Award className="h-3 w-3" />{(c.qualifications || []).slice(0, 2).join(", ") || "—"}</p>
              <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" />{c.city} · {c.service_radius_km}km · {c.years_experience}y exp</p>
              <div className="mt-2 flex items-center gap-2">
                <Badge className={`${meta.cls} border-0 text-[10px]`}>{meta.label}</Badge>
                <span className="text-[10px] text-slate-400">{c.capacity_sessions_week}/wk cap</span>
              </div>
            </div>
          );
        }}
      />
      )}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-head">{detail.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4" data-testid="clinician-detail">
                <div className="flex gap-2">
                  <Badge className="bg-ccc-navy text-white border-0">{labelOf(CLINICIAN_STAGES, detail.stage)}</Badge>
                  <Badge className={`${(CRED_META[detail.credentialing_status] || CRED_META.pending).cls} border-0`}>{(CRED_META[detail.credentialing_status] || CRED_META.pending).label}</Badge>
                </div>
                {cedit && (
                <div className="bg-white rounded-xl p-4 border border-slate-200 space-y-3" data-testid="clinician-edit">
                  <p className="text-xs font-semibold uppercase text-slate-400">Modify</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Stage</Label><Select value={cedit.stage} onValueChange={(v) => setCEdit({ ...cedit, stage: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{CLINICIAN_STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Credentialing</Label><Select value={cedit.credentialing_status} onValueChange={(v) => setCEdit({ ...cedit, credentialing_status: v })}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(CRED_META).map((s) => <SelectItem key={s} value={s}>{CRED_META[s].label}</SelectItem>)}</SelectContent></Select></div>
                    <div><Label>Capacity / wk</Label><Input type="number" value={cedit.capacity_sessions_week} onChange={(e) => setCEdit({ ...cedit, capacity_sessions_week: e.target.value })} className="mt-1" /></div>
                    <div><Label>City</Label><Select value={cedit.city || undefined} onValueChange={(v) => setCEdit({ ...cedit, city: v })}><SelectTrigger className="mt-1"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{INDIA_CITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
                    <div className="col-span-2"><Label>Notes</Label><Input value={cedit.notes} onChange={(e) => setCEdit({ ...cedit, notes: e.target.value })} className="mt-1" /></div>
                  </div>
                  <Button data-testid="save-clinician-detail" onClick={saveClinician} className="w-full bg-ccc-blue hover:bg-ccc-blue2 text-white">Save changes</Button>
                  {isAdmin && (
                    <Button data-testid="delete-clinician-button" variant="outline" onClick={remove} className="w-full border-rose-300 text-ccc-rose hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-2" />Delete clinician</Button>
                  )}
                </div>
                )}
                {detail.credentialing_flags?.length > 0 && (
                  <div className="bg-rose-50 border border-rose-100 rounded-lg p-3">
                    <p className="text-xs font-semibold text-ccc-rose flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Credentialing flags</p>
                    <ul className="mt-1 text-xs text-rose-600 list-disc pl-4">{detail.credentialing_flags.map((f) => <li key={f}>{f}</li>)}</ul>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <F label="City" value={detail.city} />
                  <F label="Experience" value={`${detail.years_experience} yrs`} />
                  <F label="Service radius" value={`${detail.service_radius_km} km`} />
                  <F label="Capacity" value={`${detail.capacity_sessions_week}/wk`} />
                  <F label="Qualifications" value={(detail.qualifications || []).join(", ")} />
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <p className="text-xs font-semibold uppercase text-slate-400 mb-1">Capacity Utilization</p>
                  <div className="flex items-center gap-3 mt-2">
                    <Progress value={Math.min(detail.utilization_pct, 100)} className="flex-1 h-2.5" />
                    <span className="text-sm font-bold font-head text-ccc-teal">{detail.utilization_pct}%</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">{detail.case_load} active cases · {detail.total_cases} total</p>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

const F = ({ label, value }) => (
  <div><p className="text-[11px] uppercase tracking-wide text-slate-400">{label}</p><p className="text-sm text-ccc-navy font-medium mt-0.5">{value || "—"}</p></div>
);
